import { useTranslation } from 'react-i18next';
import { Repeat } from 'lucide-react';

export type RecurrencePattern = 'none' | 'daily' | 'weekly' | 'biweekly' | 'monthly';

export type RecurrenceEndType = 'date' | 'count';

export interface RecurrenceFormData {
  pattern: RecurrencePattern;
  endType: RecurrenceEndType;
  endDate: string | null;
  count: number | null;
}

export interface RecurrenceFormProps {
  value: RecurrenceFormData;
  onChange: (value: RecurrenceFormData) => void;
  disabled?: boolean;
}

export function RecurrenceForm({ value, onChange, disabled }: RecurrenceFormProps) {
  const { t } = useTranslation();

  function handlePatternChange(pattern: RecurrencePattern) {
    onChange({
      ...value,
      pattern,
      // Reset end conditions when switching to 'none'
      endDate: pattern === 'none' ? null : value.endDate,
      count: pattern === 'none' ? null : value.count,
    });
  }

  function handleEndTypeChange(endType: RecurrenceEndType) {
    onChange({
      ...value,
      endType,
      // Clear the other end condition
      endDate: endType === 'date' ? value.endDate || '' : null,
      count: endType === 'count' ? value.count || 4 : null,
    });
  }

  function handleEndDateChange(endDate: string) {
    onChange({ ...value, endDate });
  }

  function handleCountChange(count: number) {
    onChange({ ...value, count });
  }

  return (
    <div className="space-y-4 rounded-lg border border-ds-line bg-ds-sunk p-4">
      <div className="flex items-center gap-2">
        <Repeat className="h-4 w-4 text-ds-muted" />
        <h4 className="text-sm font-medium text-ds-text">
          {t('appointments.recurrence.title')}
        </h4>
      </div>

      {/* Pattern Selection */}
      <div>
        <label className="block text-sm font-medium text-ds-text">
          {t('appointments.recurrence.pattern')}
        </label>
        <select
          value={value.pattern}
          onChange={(e) => handlePatternChange(e.target.value as RecurrencePattern)}
          disabled={disabled}
          className="ds-field mt-1"
        >
          <option value="none">{t('appointments.recurrence.patterns.none')}</option>
          <option value="daily">{t('appointments.recurrence.patterns.daily')}</option>
          <option value="weekly">{t('appointments.recurrence.patterns.weekly')}</option>
          <option value="biweekly">{t('appointments.recurrence.patterns.biweekly')}</option>
          <option value="monthly">{t('appointments.recurrence.patterns.monthly')}</option>
        </select>
      </div>

      {/* End Condition (only show if recurring) */}
      {value.pattern !== 'none' && (
        <>
          <div>
            <label className="block text-sm font-medium text-ds-text">
              {t('appointments.recurrence.end_condition')}
            </label>
            <div className="mt-2 space-y-2">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  checked={value.endType === 'date'}
                  onChange={() => handleEndTypeChange('date')}
                  disabled={disabled}
                  className="h-4 w-4 accent-ds-teal disabled:opacity-50"
                />
                <span className="text-sm text-ds-text">
                  {t('appointments.recurrence.end_by_date')}
                </span>
              </label>
              {value.endType === 'date' && (
                <input
                  type="date"
                  value={value.endDate || ''}
                  onChange={(e) => handleEndDateChange(e.target.value)}
                  disabled={disabled}
                  min={new Date().toISOString().split('T')[0]}
                  className="ds-field ml-6 mt-1"
                />
              )}

              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  checked={value.endType === 'count'}
                  onChange={() => handleEndTypeChange('count')}
                  disabled={disabled}
                  className="h-4 w-4 accent-ds-teal disabled:opacity-50"
                />
                <span className="text-sm text-ds-text">
                  {t('appointments.recurrence.end_after_count')}
                </span>
              </label>
              {value.endType === 'count' && (
                <div className="ml-6 mt-1 flex items-center gap-2">
                  <input
                    type="number"
                    value={value.count || ''}
                    onChange={(e) => handleCountChange(parseInt(e.target.value, 10))}
                    disabled={disabled}
                    min={1}
                    max={365}
                    className="ds-field w-24"
                  />
                  <span className="text-sm text-ds-text">
                    {t('appointments.recurrence.occurrences')}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Preview/Summary */}
          <div className="rounded-md bg-ds-teal-tint p-3 text-sm text-ds-teal">
            <p>
              {t('appointments.recurrence.summary', {
                pattern: t(`appointments.recurrence.patterns.${value.pattern}`).toLowerCase(),
                end:
                  value.endType === 'date'
                    ? t('appointments.recurrence.summary_until', {
                        date: value.endDate
                          ? new Date(value.endDate).toLocaleDateString()
                          : '—',
                      })
                    : t('appointments.recurrence.summary_count', { count: value.count || 0 }),
              })}
            </p>
          </div>
        </>
      )}
    </div>
  );
}
